/* global window */
import * as React from "react";
import PropType from "prop-types";
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { Menu, MenuPopover, MenuTrigger } from "@fluentui/react-components";
import CommonTooltip from "../../common/CommonTooltip";
import CustomColorPickerSubMenu from "./CustomColorPickerSubMenu";
import { openStyleSheetDialog, openColorPicker, closeColorPicker } from "../../../state/appStateSlice";
import TrackedButton from "../../common/TrackedButton";

// all colors unlisted here are default "base" color group
const NOCOLOR = "nocolor";
const BASE = "base";
const STATUS = "status";
const BACKGROUND = "background";
const SEQUENTIAL = "sequential";
const DIVERGING = "diverging";
const colorGroupMap = {
  none: NOCOLOR,
  success: STATUS,
  warning: STATUS,
  danger: STATUS,
  background: BACKGROUND,
  background2: BACKGROUND,
  sequential1: SEQUENTIAL,
  sequential2: SEQUENTIAL,
  sequential3: SEQUENTIAL,
  sequential4: SEQUENTIAL,
  sequential5: SEQUENTIAL,
  diverging1: DIVERGING,
  diverging2: DIVERGING,
  diverging3: DIVERGING,
  diverging4: DIVERGING,
  diverging5: DIVERGING,
};

const ColorButton = (props) => {
  const { colorName, colorValue, tooltipContent, isSelected, onNewColor } = props;
  return (
    <CommonTooltip content={tooltipContent} positioning="below">
      <TrackedButton
        id={`color_${colorName}`}
        style={{
          background: colorValue,
          width: 24,
          height: 24,
          minWidth: 0,
          padding: 0,
          margin: 0,
          border: isSelected ? "2px solid #D40000" : "1px solid #B8B8B8",
          boxSizing: "border-box",
        }}
        onClick={() => {
          onNewColor(colorValue, colorName);
        }}
      >
        {colorName === "none" && (
          <span
            style={{
              content: "",
              position: "absolute",
              borderTop: "1px solid #D40000",
              width: "29px",
              transform: "rotate(135deg)",
              transformOrigin: "90% 0%",
              translate: "-20px 10px",
            }}
          />
        )}
      </TrackedButton>
    </CommonTooltip>
  );
};

ColorButton.propTypes = {
  colorName: PropType.string,
  colorValue: PropType.string,
  tooltipContent: PropType.string,
  isSelected: PropType.bool,
  onNewColor: PropType.func,
};

const ColorPickerSubMenu = (props) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const currentStyleSheet = useSelector(
    (state) => state.styleSheet.sheets[state.styleSheet.selectedSheet] ?? state.styleSheet.sheets["default"]
  );

  // Add none color if needed to the entire color list
  const colors = React.useMemo(() => {
    if (props.enabled.includes(NOCOLOR)) {
      return [{ name: "none", value: "transparent" }, ...currentStyleSheet.colors];
    }

    return currentStyleSheet.colors;
  }, [currentStyleSheet.colors, props.enabled]);

  // Render a color button (extracted to avoid code duplication)
  const renderColorButton = (color) => {
    const isSelected =
      (props.color === color.value && (!props.colorName || props.colorName === color.name)) ||
      (props.color === "transparent" && color.value === "transparent") ||
      (props.color === "none" && color.value === "transparent");

    return (
      <div key={color.name}>
        <ColorButton
          colorName={color.name}
          colorValue={color.value}
          tooltipContent={t("menu.style.colors." + color.name)}
          isSelected={isSelected}
          onNewColor={props.onNewColor}
        />
      </div>
    );
  };

  const renderColorGroup = (colors, group) => {
    return colors
      .filter((c) => (group === BASE ? colorGroupMap[c.name] === undefined : colorGroupMap[c.name] === group))
      .map((c) => renderColorButton(c));
  };

  return (
    <div>
      <div
        id={props.id}
        style={{
          display: "grid",
          gap: 4,
          width: "100%",
        }}
      >
        {!props.onlyCustom && (
          <>
            {props.enabled.includes(NOCOLOR) || props.enabled.includes(BACKGROUND) || props.enabled.includes(BASE) ? (
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, 24px)",
                  gap: 8,
                  justifyContent: "start",
                  alignContent: "start",
                  marginBottom: 4,
                }}
              >
                {props.enabled.includes(NOCOLOR) && renderColorGroup(colors, NOCOLOR)}
                {props.enabled.includes(BACKGROUND) && renderColorGroup(colors, BACKGROUND)}
                {props.enabled.includes(BASE) && renderColorGroup(colors, BASE)}
              </div>
            ) : (
              <></>
            )}

            {props.enabled.includes(SEQUENTIAL) && (
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, 24px)",
                  gap: 8,
                  justifyContent: "start",
                  alignContent: "start",
                  marginBottom: 4,
                }}
              >
                {renderColorGroup(colors, SEQUENTIAL)}
              </div>
            )}

            {props.enabled.includes(DIVERGING) && (
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, 24px)",
                  gap: 8,
                  justifyContent: "start",
                  alignContent: "start",
                  marginBottom: 4,
                }}
              >
                {renderColorGroup(colors, DIVERGING)}
              </div>
            )}

            {props.enabled.includes(STATUS) && (
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, 24px)",
                  gap: 8,
                  justifyContent: "start",
                  alignContent: "start",
                  marginBottom: 4,
                }}
              >
                {renderColorGroup(colors, STATUS)}
              </div>
            )}
          </>
        )}

        {/* Custom color picker */}
        {(props.showCustom || props.onlyCustom) && (
          <div>
            <Menu
              onOpenChange={(e, data) => {
                if (data.open) {
                  dispatch(openColorPicker());
                } else {
                  dispatch(closeColorPicker());
                }
              }}
              positioning="after"
            >
              <MenuTrigger>
                <CommonTooltip content={t("menu.style.text.color")} positioning="below">
                  <TrackedButton
                    id={`${props.id}_custom_color`}
                    style={{
                      width: 24,
                      height: 24,
                      minWidth: 0,
                      backgroundColor: props.color ?? "black",
                      padding: 0,
                      margin: 0,
                    }}
                  />
                </CommonTooltip>
              </MenuTrigger>
              <MenuPopover style={{ minWidth: "350px", maxWidth: "400px" }}>
                <CustomColorPickerSubMenu color={props.color} onNewColor={props.onNewColor} detectedColors={[]} />
              </MenuPopover>
            </Menu>
          </div>
        )}
      </div>
      {!props.hideLink && (
        <div style={{ marginTop: 8, textAlign: "left" }}>
          <a
            href="#"
            style={{
              color: "#0065FD",
              textDecoration: "none",
              cursor: "pointer",
              fontSize: "14px",
            }}
            onClick={(e) => {
              e.preventDefault();
              dispatch(openStyleSheetDialog());
              window.setTimeout(() => {
                const tab = window.document.getElementById("colors");
                if (tab) tab.click();
              }, 100);
            }}
          >
            {t("menu.stylesheet.editStyleSheetColors")}
          </a>
        </div>
      )}
    </div>
  );
};

ColorPickerSubMenu.propTypes = {
  id: PropType.string,
  color: PropType.string,
  colorName: PropType.string,
  onNewColor: PropType.func.isRequired,
  onlyCustom: PropType.bool,
  showCustom: PropType.bool,
  sheetOverride: PropType.object,
  hideLink: PropType.bool,
  enabled: PropType.array,
};

export default ColorPickerSubMenu;
